/**
 * SandboxRequestPage — the tenant-side sandbox request surface.
 *
 * Registered ONLY in 'vertical-tenant' mode (src/shell/routes.ts). The mode split
 * (decision-2026-07-25-broker-mode-split) is what makes that statement structural
 * rather than aspirational: a meta-mode instance never registers this route, and a
 * tenant-mode instance never registers the operator provisioning route.
 *
 * Boundary discipline (adr-075 D11): this surface SUBMITS a request and DISPLAYS its
 * state. It does not provision, does not create realms, does not decommission — and
 * in tenant mode those capabilities are structurally absent, not merely forbidden.
 * The membrane image and the connection bundle are not surfaced here.
 *
 * Gate-0 (convention-app-onboarding / ADR-068, ADR-067 Amd 2) is enforced here for
 * the human, AND again server-side, which is the enforcement that counts. GitHub org
 * is required for an external app because the repo is created in the owner org at
 * provisioning time — there is no scratch-and-transfer, so it cannot be collected later.
 *
 * Approval is quota-bounded auto-approve, per-tenant ceiling 3
 * (decision-2026-07-25-sandbox-request-approval-quota). Under quota, submission emits
 * `sandbox.provision_requested` directly. Over quota, it lands in the meta-broker
 * operator queue. The quota is applied server-side; this page only reports the outcome.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../auth/KeycloakProvider';
import { canRequestSandbox } from '../auth/surfaceAccess';
import { useConfig } from '../config/ConfigContext';
import CostDisclosure, { disclosedCostBasis } from '../components/CostDisclosure';
import {
  submitSandboxRequest,
  useMySandboxRequests,
  type AppKind,
  type SandboxRequestRow,
} from '../hooks/useSandboxRequest';

/** Same slug rule as the operator register form — one rule, one regex. */
const SLUG_RE = /^[a-z][a-z0-9-]+$/;

/** The role that may request a sandbox. A plain tenant-user must not reach this. */
const TENANT_ADMIN_ROLE = 'tenant-admin';

export function deriveSlug(appName: string): string {
  return appName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: 'var(--sp-2) var(--sp-3)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body-sm)',
  color: 'var(--fg-1)',
  background: 'var(--bg-recessed)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-meta)',
  color: 'var(--fg-3)',
  marginBottom: 4,
  fontWeight: 600,
};

const helpTextStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-meta)',
  color: 'var(--fg-4)',
  marginTop: 4,
};

const errStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-meta)',
  color: 'var(--dp-crimson-600)',
  marginTop: 4,
};

const STATE_LABEL: Record<SandboxRequestRow['state'], string> = {
  pending_operator: 'Pending — operator review (over quota)',
  approved: 'Approved',
  provisioning: 'Provisioning',
  ready: 'Ready',
  declined: 'Declined',
};

function RequestAccessDenied() {
  return (
    <div
      className="dp-shell__content"
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-12)',
      }}
    >
      <p className="dp-eyebrow">Access denied</p>
      <h2>Requesting a sandbox requires tenant-admin</h2>
      <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
        Your account needs the <code>{TENANT_ADMIN_ROLE}</code> role. Ask an administrator in your
        organisation to submit the request.
      </p>
    </div>
  );
}

interface FormState {
  appName: string;
  slug: string;
  slugTouched: boolean;
  purpose: string;
  appKind: AppKind;
  githubOrg: string;
  consent: boolean;
}

type FieldErrors = Partial<Record<'appName' | 'slug' | 'purpose' | 'githubOrg' | 'consent', string>>;

export default function SandboxRequestPage() {
  const config = useConfig();
  const { token, hasRole } = useAuth();

  const [form, setForm] = useState<FormState>({
    appName: '',
    slug: '',
    slugTouched: false,
    purpose: '',
    appKind: 'platform',
    githubOrg: '',
    consent: false,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { state: mine, reload } = useMySandboxRequests(token);

  // The disclosure the human is about to agree to. `[]` — there is no rate model,
  // and no caller here may invent one.
  const disclosure = useMemo(() => disclosedCostBasis([]), []);

  // auth/surfaceAccess 2026-08-02 — same role, shared with the rail predicate.
  const canRequest = canRequestSandbox(hasRole);
  if (!canRequest) return <RequestAccessDenied />;

  const effectiveSlug = form.slugTouched ? form.slug : deriveSlug(form.appName);

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.appName.trim()) e.appName = 'App name is required.';
    if (!effectiveSlug) e.slug = 'Slug is required.';
    else if (!SLUG_RE.test(effectiveSlug))
      e.slug = 'Slug: lowercase, start with a letter, e.g. my-app (^[a-z][a-z0-9-]+$).';
    if (!form.purpose.trim()) e.purpose = 'Purpose is required — the operator reads this.';
    if (form.appKind === 'external' && !form.githubOrg.trim())
      e.githubOrg =
        'GitHub organisation is required for an external app — the repository is created in your org at provisioning and cannot be moved later.';
    if (!form.consent) e.consent = 'Please confirm you have read how this is metered.';
    return e;
  }

  async function handleSubmit() {
    setServerError(null);
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (!token) {
      setServerError('Not authenticated.');
      return;
    }

    setSubmitting(true);
    try {
      await submitSandboxRequest(token, {
        app_name: form.appName.trim(),
        slug: effectiveSlug,
        purpose: form.purpose.trim(),
        app_kind: form.appKind,
        ...(form.appKind === 'external' ? { github_org: form.githubOrg.trim() } : {}),
        disclosed_cost_basis: disclosure,
      });
      setForm({
        appName: '',
        slug: '',
        slugTouched: false,
        purpose: '',
        appKind: 'platform',
        githubOrg: '',
        consent: false,
      });
      reload();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="dp-shell__content" style={{ flex: 1, padding: 'var(--sp-8)', overflow: 'auto' }}>
      <p className="dp-eyebrow">Sandbox</p>
      <h2 style={{ marginTop: 'var(--sp-1)' }}>Request a sandbox</h2>
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--fg-3)',
          maxWidth: 680,
        }}
      >
        A sandbox is an isolated environment for {config.instance.tenant ?? config.instance.name} to
        build and test an app before it is promoted. Submitting the request starts provisioning; you
        can follow its state below.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', maxWidth: 680, marginTop: 'var(--sp-6)' }}>
        <div>
          <label style={labelStyle} htmlFor="sr-app-name">
            App name <span style={{ color: 'var(--dp-crimson-600)' }}>*</span>
          </label>
          <input
            id="sr-app-name"
            style={inputStyle}
            value={form.appName}
            onChange={(ev) => update('appName', ev.target.value)}
          />
          {errors.appName && <p style={errStyle}>{errors.appName}</p>}
        </div>

        <div>
          <label style={labelStyle} htmlFor="sr-slug">
            Slug <span style={{ color: 'var(--dp-crimson-600)' }}>*</span>
          </label>
          <input
            id="sr-slug"
            style={inputStyle}
            value={effectiveSlug}
            onChange={(ev) => {
              update('slugTouched', true);
              update('slug', ev.target.value);
            }}
          />
          <p style={helpTextStyle}>Derived from the app name; edit if you need a different one.</p>
          {errors.slug && <p style={errStyle}>{errors.slug}</p>}
        </div>

        <div>
          <label style={labelStyle} htmlFor="sr-purpose">
            Purpose <span style={{ color: 'var(--dp-crimson-600)' }}>*</span>
          </label>
          <textarea
            id="sr-purpose"
            style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }}
            value={form.purpose}
            onChange={(ev) => update('purpose', ev.target.value)}
          />
          <p style={helpTextStyle}>
            What the app is for. Read by the operator and reused as the service description.
          </p>
          {errors.purpose && <p style={errStyle}>{errors.purpose}</p>}
        </div>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={labelStyle}>App kind</legend>
          <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            <input
              type="radio"
              name="sr-app-kind"
              value="platform"
              checked={form.appKind === 'platform'}
              onChange={() => update('appKind', 'platform')}
            />{' '}
            Platform — owned and operated by do.place
          </label>
          <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            <input
              type="radio"
              name="sr-app-kind"
              value="external"
              checked={form.appKind === 'external'}
              onChange={() => update('appKind', 'external')}
            />{' '}
            External — owned by your organisation
          </label>
        </fieldset>

        {form.appKind === 'external' && (
          <div>
            <label style={labelStyle} htmlFor="sr-github-org">
              GitHub organisation <span style={{ color: 'var(--dp-crimson-600)' }}>*</span>
            </label>
            <input
              id="sr-github-org"
              style={inputStyle}
              value={form.githubOrg}
              onChange={(ev) => update('githubOrg', ev.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <p style={helpTextStyle}>
              The repository is created in this organisation at provisioning time (ADR-067 Amd 2) —
              it cannot be supplied later.
            </p>
            {errors.githubOrg && <p style={errStyle}>{errors.githubOrg}</p>}
          </div>
        )}

        <CostDisclosure costLines={[]} />

        <div>
          <label
            style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}
          >
            <input
              id="sr-consent"
              type="checkbox"
              checked={form.consent}
              onChange={(ev) => update('consent', ev.target.checked)}
            />
            <span>
              I have read the disclosure above, understand how this is metered, and agree to proceed.
            </span>
          </label>
          {errors.consent && <p style={errStyle}>{errors.consent}</p>}
        </div>

        {serverError && <p style={{ ...errStyle, fontSize: 'var(--fs-body-sm)' }}>{serverError}</p>}

        <div>
          <button
            className="dp-btn dp-btn--primary"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>

      {/* ── Request state — the submitter can follow progress without asking anyone ── */}
      <section style={{ marginTop: 'var(--sp-10)', maxWidth: 900 }}>
        <p className="dp-eyebrow">Your requests</p>
        {mine.tag === 'loading' && (
          <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            Loading…
          </p>
        )}
        {mine.tag === 'error' && (
          <p style={{ ...errStyle, fontSize: 'var(--fs-body-sm)' }}>{mine.message}</p>
        )}
        {mine.tag === 'loaded' && mine.requests.length === 0 && (
          <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            No requests yet.
          </p>
        )}
        {mine.tag === 'loaded' && mine.requests.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 'var(--sp-3)' }}>
            <thead>
              <tr style={{ textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)' }}>
                <th style={{ padding: 'var(--sp-2) var(--sp-3)' }}>App</th>
                <th style={{ padding: 'var(--sp-2) var(--sp-3)' }}>Kind</th>
                <th style={{ padding: 'var(--sp-2) var(--sp-3)' }}>State</th>
                <th style={{ padding: 'var(--sp-2) var(--sp-3)' }}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {mine.requests.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                  <td style={{ padding: 'var(--sp-3)' }}>
                    {r.app_name}
                    <br />
                    <code style={{ fontSize: 'var(--fs-meta)', color: 'var(--fg-4)' }}>{r.slug}</code>
                  </td>
                  <td style={{ padding: 'var(--sp-3)' }}>{r.app_kind}</td>
                  <td style={{ padding: 'var(--sp-3)' }}>
                    {STATE_LABEL[r.state] ?? r.state}
                    {r.state === 'declined' && r.decline_reason && (
                      <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>{r.decline_reason}</div>
                    )}
                  </td>
                  <td style={{ padding: 'var(--sp-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)' }}>
                    {r.created_at}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
